import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EASE_EXPO_OUT } from '@/lib/motion';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { SegmentedControl } from '@/components/ui/segmented-control';
import {
  TimelineBar,
  ViolationSpans,
  TimeAxis,
  computeZoneLayout,
  type TimelineZone,
  type ViolationSpan,
  type TimeAxisTick,
} from '@/components/config_forms/shared/help-graphics/timeline/TimelinePrimitives';
import {
  TIMELINE_SEGMENT_HEADING_CLASS,
  formatDeadlineTick,
} from '@/components/config_forms/shared/help-graphics/timeline/timelineTextTokens';
import { ScenarioTogglePanel, type ScenarioOption } from '@/components/config_forms/shared/ScenarioTogglePanel';
import { ControlRow } from '@/components/config_forms/shared/ControlRow';
import { useMethodSwapAnimation } from '../surcharge/useMethodSwapAnimation';
import { SurchargeMethodPanel } from '../surcharge/SurchargeMethodPanel';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface FixedFeeTimelineProps {
  feeSchedule: { fee_amount: number | null }[];
  deadlineHours: number;
  recurringDeadline: boolean;
  mealTriggerMode: 'deadline' | 'count';
  requiredBreaks: number;
  scalingEnabled: boolean;
  onFeeScheduleChange?: (schedule: { fee_amount: number | null }[]) => void;
  fixedFeeStructureMode?: 'single' | 'schedule';
  onFixedFeeStructureModeChange?: (mode: 'single' | 'schedule') => void;
  controlSlotContent?: React.ReactNode;
  singleControlSlotContent?: React.ReactNode;
}

interface TimelinePanel {
  zones: TimelineZone[];
  spans: ViolationSpan[];
  ticks: TimeAxisTick[];
  totalFee: number;
  totalDimmed: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function resolveFee(fee: number | null | undefined): number {
  if (typeof fee !== 'number' || !Number.isFinite(fee)) return 0;
  return fee;
}

function isFeeConfigured(fee: number | null | undefined): boolean {
  return typeof fee === 'number' && Number.isFinite(fee);
}

function getFeeForViolation(
  n: number,
  feeSchedule: { fee_amount: number | null }[],
): { resolved: number; configured: boolean } {
  const idx = Math.min(n, feeSchedule.length) - 1;
  const raw = feeSchedule[idx]?.fee_amount;
  return { resolved: resolveFee(raw), configured: isFeeConfigured(raw) };
}

function formatFee(amount: number): string {
  return `$${amount}`;
}



/* ------------------------------------------------------------------ */
/*  Zone content helpers                                               */
/* ------------------------------------------------------------------ */

const ZoneLabel: React.FC<{ text: string; variant: 'safe' | 'break' | 'ellipsis' }> = ({ text, variant }) => {
  const colorMap = {
    safe: 'color-mix(in srgb, var(--color-scenario-green), white 35%)',
    break: 'color-mix(in srgb, var(--color-scenario-green), white 40%)',
    ellipsis: 'var(--destructive-text)',
  };
  return (
    <span
      className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.3px] pointer-events-none whitespace-nowrap overflow-hidden text-ellipsis"
      style={{ color: colorMap[variant] }}
    >
      {text}
    </span>
  );
};

const FeeContent: React.FC<{
  feeValue?: string;
  feeSeq: string;
  feeSeqSuffix?: string;
  variant: TimelineZone['variant'];
}> = ({ feeValue, feeSeq, feeSeqSuffix, variant }) => {
  const feeColor = variant === 'safe' || variant === 'break'
    ? 'var(--scenario-green)'
    : variant === 'v1'
      ? 'var(--primary-text)'
      : 'var(--destructive-text)';
  const seqColor = variant === 'v1'
    ? 'var(--primary-text)'
    : 'var(--destructive-text)';
  return (
    <>
      {feeValue && (
        <span
          className="font-mono text-[10px] sm:text-[12px] font-semibold leading-none pointer-events-none whitespace-nowrap overflow-hidden text-ellipsis"
          style={{ color: feeColor }}
        >
          {feeValue}
        </span>
      )}
      <span
        className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.2px] pointer-events-none whitespace-nowrap overflow-hidden text-ellipsis"
        style={{ color: seqColor }}
      >
        {feeSeq}
      </span>
      {feeSeqSuffix && (
        <span
          className="font-label text-[9px] leading-none uppercase tracking-[0.2px] pointer-events-none"
          style={{ color: seqColor }}
        >
          ({feeSeqSuffix})
        </span>
      )}
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Panel computation — single (non-recurring) deadline mode           */
/* ------------------------------------------------------------------ */

function computeSinglePanel(
  feeSchedule: { fee_amount: number | null }[],
  deadlineHours: number,
): TimelinePanel {
  const fee1 = getFeeForViolation(1, feeSchedule);

  const zones: TimelineZone[] = [
    {
      id: 'safe',
      flex: 3,
      variant: 'safe',
      content: <ZoneLabel text="Working" variant="safe" />,
    },
    {
      id: 'v1',
      flex: 2.5,
      variant: 'v1',
      borderRightOverride: 'border-r-0',
      content: fee1.configured
        ? <FeeContent feeValue={formatFee(fee1.resolved)} feeSeq="fee #1" variant="v1" />
        : <FeeContent feeSeq="fee #1" variant="v1" />,
    },
  ];

  const { lefts } = computeZoneLayout(zones, { minPct: 13 });

  const spans: ViolationSpan[] = [
    {
      id: 'v1-span',
      label: 'Violation 1',
      leftPct: lefts[1],
      rightPct: 0,
      variant: 'v1',
    },
  ];

  const ticks: TimeAxisTick[] = [
    { id: 'start', label: 'Start', leftPct: 0, align: 'left', variant: 'label' },
    {
      id: 'deadline',
      label: formatDeadlineTick(deadlineHours),
      leftPct: lefts[1],
      align: 'center',
      variant: 'amber',
    },
    { id: 'end', label: 'End', leftPct: 100, align: 'right', variant: 'label' },
  ];

  return {
    zones,
    spans,
    ticks,
    totalFee: fee1.resolved,
    totalDimmed: !fee1.configured,
  };
}

/* ------------------------------------------------------------------ */
/*  Panel computation — recurring (no-break) panel                     */
/* ------------------------------------------------------------------ */

function computeRecurringNoBreakPanel(
  feeSchedule: { fee_amount: number | null }[],
  deadlineHours: number,
): TimelinePanel {
  // Zone count: min(feeSchedule.length + 1, 5) violation zones + safe + ellipsis
  const maxViolations = Math.min(feeSchedule.length + 1, 5);

  const zones: TimelineZone[] = [
    {
      id: 'safe',
      flex: 2.5,
      variant: 'safe',
      content: <ZoneLabel text="Working" variant="safe" />,
    },
  ];

  let totalFee = 0;
  let allDimmed = true;

  for (let v = 1; v <= maxViolations; v++) {
    const fee = getFeeForViolation(v, feeSchedule);
    const isRepeat = v > feeSchedule.length;
    totalFee += fee.resolved;
    if (fee.configured) allDimmed = false;

    const variant: TimelineZone['variant'] = v === 1 ? 'v1' : v % 2 === 0 ? 'v2' : 'v2-alt';
    const seqLabel = `fee #${isRepeat ? feeSchedule.length : v}`;
    const seqSuffix = isRepeat ? 'repeat' : undefined;

    zones.push({
      id: `v${v}`,
      flex: 1.5,
      variant,
      content: fee.configured
        ? <FeeContent feeValue={formatFee(fee.resolved)} feeSeq={seqLabel} feeSeqSuffix={seqSuffix} variant={variant} />
        : <FeeContent feeSeq={seqLabel} feeSeqSuffix={seqSuffix} variant={variant} />,
      isDeadlineBoundary: true,
    });
  }

  // Ellipsis zone
  zones.push({
    id: 'ellipsis',
    flex: 1,
    variant: 'ellipsis',
    content: <ZoneLabel text="..." variant="ellipsis" />,
  });

  const { widths, lefts } = computeZoneLayout(zones, { minPct: 13 });

  // Spans: one per violation zone + one for ellipsis
  const spans: ViolationSpan[] = [];
  for (let v = 1; v <= maxViolations; v++) {
    const zoneIdx = v; // safe is index 0
    const nextLeft = zoneIdx + 1 < zones.length ? lefts[zoneIdx + 1] : lefts[zoneIdx] + widths[zoneIdx];
    const rightPct = 100 - nextLeft;
    spans.push({
      id: `v${v}-span`,
      label: `V${v}`,
      leftPct: lefts[zoneIdx],
      rightPct,
      variant: v === 1 ? 'v1' : 'v2',
    });
  }
  // Ellipsis span
  const ellipsisIdx = zones.length - 1;
  spans.push({
    id: 'ellipsis-span',
    label: `V${maxViolations + 1}+`,
    leftPct: lefts[ellipsisIdx],
    rightPct: 0,
    variant: 'v2',
  });

  // Ticks — every fee-zone left edge marks a deadline boundary.
  // Show cumulative deadlines from shift start (+Nh, +2Nh, ...).
  const ticks: TimeAxisTick[] = [
    { id: 'start', label: 'Start', leftPct: 0, align: 'left', variant: 'label' },
  ];
  for (let v = 0; v < maxViolations; v++) {
    // Violation zone v sits at index (v + 1); its left edge is lefts[v + 1].
    ticks.push({
      id: `deadline-${v}`,
      label: formatDeadlineTick((v + 1) * deadlineHours),
      leftPct: lefts[v + 1],
      align: 'center',
      variant: 'amber',
    });
  }
  ticks.push({ id: 'end', label: 'End', leftPct: 100, align: 'right', variant: 'label' });

  return { zones, spans, ticks, totalFee, totalDimmed: allDimmed };
}

/* ------------------------------------------------------------------ */
/*  Panel computation — recurring with-break: segment 1                */
/* ------------------------------------------------------------------ */

function computeRecurringWithBreakSegment1(
  feeSchedule: { fee_amount: number | null }[],
  deadlineHours: number,
): TimelinePanel {
  const fee1 = getFeeForViolation(1, feeSchedule);

  const zones: TimelineZone[] = [
    {
      id: 'safe-s1',
      flex: 2.5,
      variant: 'safe',
      content: <ZoneLabel text="Working" variant="safe" />,
    },
    {
      id: 'v1-s1',
      flex: 2,
      variant: 'v1',
      borderRightOverride: 'border-r-0',
      content: fee1.configured
        ? <FeeContent feeValue={formatFee(fee1.resolved)} feeSeq="fee #1" variant="v1" />
        : <FeeContent feeSeq="fee #1" variant="v1" />,
    },
    {
      id: 'break-s1',
      flex: 1.5,
      variant: 'break',
      content: <ZoneLabel text="Break" variant="break" />,
    },
  ];

  const { lefts } = computeZoneLayout(zones, { minPct: 13 });

  const spans: ViolationSpan[] = [
    {
      id: 'v1-s1-span',
      label: 'V1',
      leftPct: lefts[1],
      rightPct: 100 - lefts[2],
      variant: 'v1',
    },
  ];

  const ticks: TimeAxisTick[] = [
    { id: 'start-s1', label: 'Start', leftPct: 0, align: 'left', variant: 'label' },
    {
      id: 'deadline-s1',
      label: formatDeadlineTick(deadlineHours),
      leftPct: lefts[1],
      align: 'center',
      variant: 'amber',
    },
    { id: 'break-s1', label: 'Break', leftPct: lefts[2], align: 'center', variant: 'green' },
  ];

  return {
    zones,
    spans,
    ticks,
    totalFee: fee1.resolved,
    totalDimmed: !fee1.configured,
  };
}

/* ------------------------------------------------------------------ */
/*  Panel computation — recurring with-break: segment 2                */
/* ------------------------------------------------------------------ */

function computeRecurringWithBreakSegment2(
  feeSchedule: { fee_amount: number | null }[],
  deadlineHours: number,
): TimelinePanel {
  // Segment 2 starts at fee #2 (V1 consumed in segment 1)
  // Zone count for segment 2: min(feeSchedule.length + 1, 5) - 1 violation zones
  const maxViolationsTotal = Math.min(feeSchedule.length + 1, 5);
  const seg2Violations = maxViolationsTotal - 1; // V1 consumed in segment 1

  const zones: TimelineZone[] = [
    {
      id: 'safe-s2',
      flex: 2.5,
      variant: 'safe',
      content: <ZoneLabel text="Working" variant="safe" />,
    },
  ];

  let totalFee = 0;
  let allDimmed = true;

  for (let i = 0; i < seg2Violations; i++) {
    const globalV = i + 2; // Global numbering continues: V2, V3, V4...
    const fee = getFeeForViolation(globalV, feeSchedule);
    const isRepeat = globalV > feeSchedule.length;
    totalFee += fee.resolved;
    if (fee.configured) allDimmed = false;

    const variant: TimelineZone['variant'] = globalV === 2 ? 'v2' : globalV % 2 === 0 ? 'v2' : 'v2-alt';
    const seqLabel = `fee #${isRepeat ? feeSchedule.length : globalV}`;
    const seqSuffix = isRepeat ? 'repeat' : undefined;

    zones.push({
      id: `v${globalV}-s2`,
      flex: 1.5,
      variant,
      content: fee.configured
        ? <FeeContent feeValue={formatFee(fee.resolved)} feeSeq={seqLabel} feeSeqSuffix={seqSuffix} variant={variant} />
        : <FeeContent feeSeq={seqLabel} feeSeqSuffix={seqSuffix} variant={variant} />,
      isDeadlineBoundary: true,
    });
  }

  // Ellipsis zone
  zones.push({
    id: 'ellipsis-s2',
    flex: 1,
    variant: 'ellipsis',
    content: <ZoneLabel text="..." variant="ellipsis" />,
  });

  const { widths, lefts } = computeZoneLayout(zones, { minPct: 13 });

  // Spans
  const spans: ViolationSpan[] = [];
  for (let i = 0; i < seg2Violations; i++) {
    const globalV = i + 2;
    const zoneIdx = i + 1; // safe is index 0
    const nextLeft = zoneIdx + 1 < zones.length ? lefts[zoneIdx + 1] : lefts[zoneIdx] + widths[zoneIdx];
    const rightPct = 100 - nextLeft;
    spans.push({
      id: `v${globalV}-s2-span`,
      label: `V${globalV}`,
      leftPct: lefts[zoneIdx],
      rightPct,
      variant: 'v2',
    });
  }
  // Ellipsis span
  const ellipsisIdx = zones.length - 1;
  spans.push({
    id: 'ellipsis-s2-span',
    label: `V${seg2Violations + 2}+`,
    leftPct: lefts[ellipsisIdx],
    rightPct: 0,
    variant: 'v2',
  });

  // Ticks — every fee-zone left edge marks a deadline boundary in segment 2.
  // Show cumulative deadlines from break end (+Nh, +2Nh, ...).
  const ticks: TimeAxisTick[] = [
    { id: 'resume-s2', label: 'Break End', leftPct: 0, align: 'left', variant: 'label' },
  ];
  for (let i = 0; i < seg2Violations; i++) {
    ticks.push({
      id: `deadline-s2-${i}`,
      label: formatDeadlineTick((i + 1) * deadlineHours),
      leftPct: lefts[i + 1],
      align: 'center',
      variant: 'amber',
    });
  }
  ticks.push({ id: 'end-s2', label: 'End', leftPct: 100, align: 'right', variant: 'label' });

  return { zones, spans, ticks, totalFee, totalDimmed: allDimmed };
}

/* -- Total row ---------------------------------------------------- */

const TotalRow: React.FC<{ totalFee: number; dimmed: boolean }> = ({ totalFee, dimmed }) => (
  <div className="flex items-center justify-end gap-2 mt-2">
    <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[1px] text-foreground/80">
      Total Surcharge
    </span>
    <span
      className={cn(
        'inline-flex items-center justify-center font-mono text-[10px] sm:text-[12px] font-semibold min-w-[40px] h-[22px] px-2 rounded-[0.25rem] border',
        dimmed
          ? 'text-foreground/65 border-border/40 bg-surface-2/30'
          : 'text-primary-text border-primary/25 bg-primary/[0.06]',
      )}
    >
      {formatFee(totalFee)}
    </span>
  </div>
);

/* -- Compact total chip (tucked under Shift End on the axis row) - */

const TotalChip: React.FC<{ totalFee: number; dimmed: boolean }> = ({ totalFee, dimmed }) => (
  <span
    className={cn(
      'inline-flex items-center justify-center gap-2 h-[22px] min-w-[86px] px-2 rounded-[0.25rem] border whitespace-nowrap',
      dimmed
        ? 'border-border/30 bg-surface-2/30'
        : 'border-primary/25 bg-primary/[0.06]',
    )}
  >
    <span
      className={cn(
        'font-label text-[10px] sm:text-[11px] uppercase tracking-[0.8px] leading-[1.1]',
        dimmed ? 'text-foreground/65' : 'text-primary-text/90',
      )}
    >
      Total
    </span>
    <span
      className={cn(
        'font-mono text-[10px] sm:text-[11px] font-semibold leading-[1.1]',
        dimmed ? 'text-foreground/65' : 'text-primary-text',
      )}
    >
      {formatFee(totalFee)}
    </span>
  </span>
);

/* ------------------------------------------------------------------ */
/*  Time-agnostic callout                                              */
/* ------------------------------------------------------------------ */

const calloutCardClass = cn(
  'flex items-start gap-2 rounded-[0.25rem] border px-2 py-2 mt-2',
  'border-primary/10 bg-primary/[0.03]',
);

const TimeAgnosticCallout: React.FC = () => (
  <div className={calloutCardClass}>
    <span className="text-[10px] sm:text-[12px] leading-none shrink-0 mt-1">&#128161;</span>
    <span className="font-body text-[10px] sm:text-[12px] leading-[1.5] text-foreground/80">
      <strong className="font-medium text-primary-text">Time-agnostic:</strong> Fee amounts are flat regardless of how long the break was missed. A 30-minute delay costs the same as a 3-hour delay.
    </span>
  </div>
);

const ScheduleRepeatCallout: React.FC = () => (
  <div className={calloutCardClass}>
    <span className="text-[10px] sm:text-[12px] leading-none shrink-0 mt-1">&#8635;</span>
    <span className="font-body text-[10px] sm:text-[12px] leading-[1.5] text-foreground/80">
      If you run out of schedule entries, the last fee repeats.
    </span>
  </div>
);

const NoProrationCallout: React.FC = () => (
  <div className={cn(
    'flex items-start gap-2 rounded-[0.25rem] border px-2 py-2 mt-2',
    'border-destructive/10 bg-destructive/[0.06]',
  )}>
    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-1 text-destructive-text/85" aria-hidden="true" />
    <span className="font-body text-[10px] sm:text-[12px] leading-[1.5] text-foreground/80">
      <strong className="font-medium text-destructive-text/85">No proration:</strong>{' '}
      Once the deadline passes without a qualifying break, the full fee applies — even if the shift ends or the break is taken right after.
    </span>
  </div>
);

const FixedFeeCallouts: React.FC<{ showScheduleRepeat: boolean; showNoProration?: boolean }> = ({
  showScheduleRepeat,
  showNoProration = false,
}) => (
  <>
    <TimeAgnosticCallout />
    {showNoProration && <NoProrationCallout />}
    <div data-anim-audit="fixed-fee-repeat-callout-container">
      <AnimatedCollapse open={showScheduleRepeat} duration={250}>
        <div data-anim-audit="fixed-fee-repeat-callout">
          <ScheduleRepeatCallout />
        </div>
      </AnimatedCollapse>
    </div>
  </>
);

/* ------------------------------------------------------------------ */
/*  Count mode: segmented break bar                                    */
/* ------------------------------------------------------------------ */

interface CountSegment {
  key: string;
  type: 'taken' | 'missed';
  /** 1-based missed index (undefined for taken) */
  missedIndex?: number;
  fee?: { resolved: number; configured: boolean };
}

function computeCountSegments(
  feeSchedule: { fee_amount: number | null }[],
  requiredBreaks: number,
  scalingEnabled: boolean,
): {
  segments: CountSegment[];
  totalSegments: number;
  deficit: number;
  isTruncated: boolean;
  uncappedSegments: number;
  totalFee: number;
  totalDimmed: boolean;
} {
  const safeRequiredBreaks = Math.max(1, Number.isFinite(requiredBreaks) ? requiredBreaks : 1);
  const uncappedSegments = scalingEnabled
    ? Math.max(safeRequiredBreaks, feeSchedule.length + 2)
    : safeRequiredBreaks;
  const totalSegments = scalingEnabled ? Math.min(uncappedSegments, 6) : uncappedSegments;
  const deficit = totalSegments - 1;
  const isTruncated = scalingEnabled && uncappedSegments > 6;

  const segments: CountSegment[] = [
    { key: 'taken', type: 'taken' },
  ];

  let totalFee = 0;
  let allDimmed = true;

  for (let i = 1; i <= deficit; i++) {
    const fee = getFeeForViolation(i, feeSchedule);
    totalFee += fee.resolved;
    if (fee.configured) allDimmed = false;
    segments.push({
      key: `missed-${i}`,
      type: 'missed',
      missedIndex: i,
      fee,
    });
  }

  return { segments, totalSegments, deficit, isTruncated, uncappedSegments, totalFee, totalDimmed: allDimmed };
}

const CountModeBar: React.FC<{
  feeSchedule: { fee_amount: number | null }[];
  requiredBreaks: number;
  scalingEnabled: boolean;
  showScheduleRepeat: boolean;
}> = ({ feeSchedule, requiredBreaks, scalingEnabled, showScheduleRepeat }) => {
  const { segments, totalSegments, deficit, isTruncated, uncappedSegments, totalFee, totalDimmed } =
    computeCountSegments(feeSchedule, requiredBreaks, scalingEnabled);

  return (
    <>
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <span className={`${TIMELINE_SEGMENT_HEADING_CLASS} text-foreground/85`}>
          Required: {totalSegments} {totalSegments === 1 ? 'break' : 'breaks'}
        </span>
        <span className="font-label text-[10px] sm:text-[12px] uppercase tracking-[0.5px] text-foreground/80">
          Taken: 1 · Missed: {deficit}
        </span>
      </div>

      {/* Segmented bar */}
      <div
        className="relative h-14 rounded-[0.25rem] overflow-hidden flex items-stretch"
        style={{
          boxShadow:
            'inset 0 0 0 1px color-mix(in srgb, var(--border), transparent 40%)',
        }}
      >
        <AnimatePresence initial={false}>
          {segments.map((seg, i) => {
            const isLast = i === segments.length - 1;

            if (seg.type === 'taken') {
              return (
                <motion.div
                  key={seg.key}
                  layout
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.22, ease: EASE_EXPO_OUT }}
                  className={cn(
                    'flex-1 flex flex-col items-center justify-center min-w-0 gap-1 px-1 bg-scenario-green/[0.08]',
                    !isLast && 'border-r',
                  )}
                  style={{
                    borderRightColor: !isLast
                      ? 'color-mix(in srgb, var(--border), transparent 60%)'
                      : undefined,
                  }}
                >
                  <span
                    className="text-[14px] leading-none"
                    style={{ color: 'var(--color-scenario-green)' }}
                  >
                    &#10003;
                  </span>
                  <span
                    className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.3px]"
                    style={{ color: 'color-mix(in srgb, var(--color-scenario-green), transparent 20%)' }}
                  >
                    Provided
                  </span>
                </motion.div>
              );
            }

            // Missed segment
            const fee = seg.fee!;
            return (
              <motion.div
                key={seg.key}
                layout
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22, ease: EASE_EXPO_OUT }}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center min-w-0 gap-1 px-1 bg-primary/[0.06]',
                  !isLast && 'border-r',
                )}
                style={{
                  borderRightColor: !isLast
                    ? 'color-mix(in srgb, var(--border), transparent 60%)'
                    : undefined,
                }}
              >
                {fee.configured && (
                  <span
                    className="font-mono text-[10px] sm:text-[12px] font-bold leading-none pointer-events-none whitespace-nowrap overflow-hidden text-ellipsis"
                    style={{ color: 'var(--primary-text)' }}
                  >
                    {formatFee(fee.resolved)}
                  </span>
                )}
                <span
                  className="font-label text-[10px] sm:text-[12px] leading-none uppercase tracking-[0.3px] pointer-events-none whitespace-nowrap overflow-hidden text-ellipsis"
                  style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 10%)' }}
                >
                  Missed #{seg.missedIndex} · fee #{Math.min(seg.missedIndex!, feeSchedule.length)}
                </span>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Truncation note */}
      {isTruncated && (
        <p className="font-sans text-[10px] sm:text-[12px] leading-none text-foreground/75 mt-2 text-center">
          Showing first {totalSegments - 1} of {uncappedSegments - 1} possible missed breaks
        </p>
      )}

      {/* Total row */}
      <div data-anim-audit="fixed-fee-total-row-collapse">
        <AnimatedCollapse open={!totalDimmed} duration={250} className="w-full">
          <div data-anim-audit="fixed-fee-total-row">
            <TotalRow totalFee={totalFee} dimmed={totalDimmed} />
          </div>
        </AnimatedCollapse>
      </div>

      {/* Time-agnostic + schedule-repeat callouts */}
      <FixedFeeCallouts showScheduleRepeat={showScheduleRepeat} />
    </>
  );
};

/* ------------------------------------------------------------------ */
/*  Exported component                                                 */
/* ------------------------------------------------------------------ */

export const FixedFeeTimeline: React.FC<FixedFeeTimelineProps> = ({
  feeSchedule,
  deadlineHours,
  recurringDeadline,
  mealTriggerMode,
  requiredBreaks,
  scalingEnabled,
  onFeeScheduleChange,
  fixedFeeStructureMode,
  onFixedFeeStructureModeChange,
  controlSlotContent,
  singleControlSlotContent,
}) => {
  const [recurringScenario, setRecurringScenario] = React.useState<'no-break' | 'break'>('no-break');

  const timelineModeKey = mealTriggerMode === 'count' ? 'count' : 'deadline';
  const {
    containerStyle: modeContainerStyle,
    containerRef: modeContainerRef,
    activePanelRef: modePanelRef,
    fadeDuration: modeFadeDuration,
  } = useMethodSwapAnimation(timelineModeKey);

  const deadlineLayoutKey = recurringDeadline ? 'recurring' : 'single';
  const {
    containerStyle: deadlineContainerStyle,
    containerRef: deadlineContainerRef,
    activePanelRef: deadlinePanelRef,
    fadeDuration: deadlineFadeDuration,
  } = useMethodSwapAnimation(deadlineLayoutKey);

  const {
    containerStyle: recurringContainerStyle,
    containerRef: recurringContainerRef,
    activePanelRef: recurringPanelRef,
    fadeDuration: recurringFadeDuration,
  } = useMethodSwapAnimation(recurringScenario);

  const inferredFeeStructureMode: 'single' | 'schedule' =
    (feeSchedule?.length ?? 0) > 1 ? 'schedule' : 'single';
  const [localFeeStructureMode, setLocalFeeStructureMode] = React.useState<'single' | 'schedule'>(
    inferredFeeStructureMode,
  );

  React.useEffect(() => {
    if (fixedFeeStructureMode == null) {
      setLocalFeeStructureMode(inferredFeeStructureMode);
    }
  }, [fixedFeeStructureMode, inferredFeeStructureMode]);

  const activeFeeStructureMode = fixedFeeStructureMode ?? localFeeStructureMode;
  const setFeeStructureMode = (mode: 'single' | 'schedule') => {
    if (fixedFeeStructureMode == null) {
      setLocalFeeStructureMode(mode);
    }
    onFixedFeeStructureModeChange?.(mode);
  };

  // Graphic should follow active fee-structure mode, not always the full schedule.
  const graphicFeeSchedule =
    activeFeeStructureMode === 'single'
      ? [{ fee_amount: feeSchedule[0]?.fee_amount ?? null }]
      : feeSchedule;

  // Determine configured state for schedule/single/graphic views
  const hasConfiguredScheduleFee = feeSchedule.some((f) => isFeeConfigured(f.fee_amount));
  const hasConfiguredSingleFee = isFeeConfigured(feeSchedule[0]?.fee_amount);
  const canToggleFeeStructure = Boolean(onFeeScheduleChange && singleControlSlotContent);
  const showingSingleFeeCard = canToggleFeeStructure && activeFeeStructureMode === 'single';
  const scheduleDetailContent = controlSlotContent ?? (
    <div className="rounded-[0.25rem] border border-dashed border-border/30 px-3 py-2">
      <span className="font-mono text-[10px] sm:text-[12px] text-muted-foreground/35">
        Editor placeholder
      </span>
    </div>
  );
  const singleDetailContent = singleControlSlotContent ?? scheduleDetailContent;
  const isFirstScheduleAddState = !showingSingleFeeCard && feeSchedule.length === 0;
  const detailCardAnimationKey = showingSingleFeeCard ? 'single' : `schedule-${feeSchedule.length}`;
  const {
    containerStyle: detailContainerStyle,
    containerRef: detailContainerRef,
    activePanelRef: detailPanelRef,
    fadeDuration: detailFadeDuration,
  } = useMethodSwapAnimation(detailCardAnimationKey);

  const singleDetailDescription = hasConfiguredSingleFee
    ? 'Charge the same fee for every missed break, regardless of break count.'
    : 'No fees configured — add at least one fee.';
  const scheduleDetailDescription = hasConfiguredScheduleFee
    ? 'Define the fee for each successive missed break. The last fee repeats.'
    : 'No fees configured — add at least one fee.';
  const scheduleCardOrientation: 'horizontal' | 'vertical' = isFirstScheduleAddState
    ? 'horizontal'
    : 'vertical';

  // Build control cards (render below the graphic when onFeeScheduleChange is provided)
  const controlSlot = onFeeScheduleChange ? (
    <div className="mt-3 rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20" data-anim-audit="fixed-fee-control-slot">
      {canToggleFeeStructure && (
        <ControlRow label="Fee Structure" description="Choose a single repeating fee or an escalating fee schedule." layout="responsive" data-testid="fixed-fee-structure-card" data-anim-audit="fixed-fee-structure-card">
          <SegmentedControl
            ariaLabel="Fee Structure"
            options={[
              { value: 'single', label: 'Fee Per Violation' },
              { value: 'schedule', label: 'Fee Schedule' },
            ]}
            value={activeFeeStructureMode}
            onChange={(value) => setFeeStructureMode(value as 'single' | 'schedule')}
            className="min-w-[220px] w-auto"
            size="sm"
          />
        </ControlRow>
      )}

      <div data-testid="fixed-fee-detail-card" data-anim-audit="fixed-fee-detail-card">
        <div
          ref={detailContainerRef}
          className="overflow-hidden w-full"
          style={detailContainerStyle}
          data-anim-audit="fixed-fee-detail-content-container"
        >
          <div className="relative grid">
            <AnimatePresence mode="sync" initial={false}>
              {showingSingleFeeCard ? (
                <SurchargeMethodPanel
                  key="detail-single"
                  activePanelRef={detailPanelRef}
                  transitionDuration={detailFadeDuration}
                >
                  <div
                    data-anim-audit="fixed-fee-detail-content"
                    data-anim-audit-detail-mode="single"
                    className="overflow-hidden"
                  >
                    <ControlRow label="Fee Per Violation" description={singleDetailDescription}>
                      {singleDetailContent}
                    </ControlRow>
                  </div>
                </SurchargeMethodPanel>
              ) : (
                <SurchargeMethodPanel
                  key={`detail-schedule-${feeSchedule.length}`}
                  activePanelRef={detailPanelRef}
                  transitionDuration={detailFadeDuration}
                >
                  <div
                    data-anim-audit="fixed-fee-detail-content"
                    data-anim-audit-detail-mode="schedule"
                    className="overflow-hidden"
                  >
                    <ControlRow
                      layout={scheduleCardOrientation === 'horizontal' ? 'row' : 'stack'}
                      label="Fee Schedule"
                      description={scheduleDetailDescription}
                    >
                      {scheduleDetailContent}
                    </ControlRow>
                  </div>
                </SurchargeMethodPanel>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // ---- Timeline mode swap ----
  // Guard: zero/negative deadline in deadline mode → render nothing (no shell)
  const isCountMode = timelineModeKey === 'count';
  if (!isCountMode && deadlineHours <= 0) return null;

  // Build content based on mode
  const singleContent = (() => {
    if (isCountMode) return null;
    const panel = computeSinglePanel(graphicFeeSchedule, deadlineHours);
    return (
      <>
        <ViolationSpans spans={panel.spans} stacked={false} />
        <TimelineBar zones={panel.zones} layoutOpts={{ minPct: 13 }} />
        <TimeAxis
          ticks={panel.ticks}
          endContent={
            <div data-anim-audit="fixed-fee-total-chip-collapse">
              <AnimatedCollapse open={!panel.totalDimmed} duration={250} className="w-full">
                <div className="flex justify-center">
                  <TotalChip totalFee={panel.totalFee} dimmed={panel.totalDimmed} />
                </div>
              </AnimatedCollapse>
            </div>
          }
        />
        <FixedFeeCallouts showScheduleRepeat={activeFeeStructureMode === 'schedule'} showNoProration />
      </>
    );
  })();

  const recurringContent = (() => {
    if (isCountMode) return null;
    const noBreakPanel = computeRecurringNoBreakPanel(graphicFeeSchedule, deadlineHours);
    const seg1 = computeRecurringWithBreakSegment1(graphicFeeSchedule, deadlineHours);
    const seg2 = computeRecurringWithBreakSegment2(graphicFeeSchedule, deadlineHours);
    const withBreakTotal = seg1.totalFee + seg2.totalFee;
    const withBreakDimmed = seg1.totalDimmed && seg2.totalDimmed;

    const scheduleMode = activeFeeStructureMode === 'schedule';
    const scenarioOptions = [
      {
        key: 'no-break' as const,
        label: 'No Break Taken',
        symbol: '\u26A0',
        description: scheduleMode
          ? 'Fees accumulate — each violation adds the next fee in the schedule'
          : 'Fees accumulate — each violation charges the same fee',
      },
      {
        key: 'break' as const,
        label: 'Break Taken',
        symbol: '\u2713',
        description: scheduleMode
          ? 'Break resets the deadline — fee schedule continues from where it left off'
          : 'Break resets the deadline — the same fee still applies to later violations',
      },
    ] satisfies ScenarioOption<'no-break' | 'break'>[];

    return (
      <>
        <ScenarioTogglePanel<'no-break' | 'break'>
          ariaLabel="Recurring Fee Scenario"
          options={scenarioOptions}
          scenario={recurringScenario}
          onScenarioChange={setRecurringScenario}
        >
        <div
          ref={recurringContainerRef}
          className="overflow-hidden"
          style={recurringContainerStyle}
          data-anim-audit="fixed-fee-recurring-scenario-container"
          data-anim-audit-scenario={recurringScenario}
        >
          <div className="relative grid">
            <AnimatePresence mode="sync" initial={false}>
              {recurringScenario === 'no-break' && (
                <SurchargeMethodPanel
                  key="no-break"
                  activePanelRef={recurringPanelRef}
                  transitionDuration={recurringFadeDuration}
                >
                  <ViolationSpans spans={noBreakPanel.spans} stacked={false} />
                  <TimelineBar zones={noBreakPanel.zones} layoutOpts={{ minPct: 13 }} />
                  <TimeAxis
                    ticks={noBreakPanel.ticks}
                    endContent={
                      <div data-anim-audit="fixed-fee-total-chip-collapse">
                        <AnimatedCollapse open={!noBreakPanel.totalDimmed} duration={250} className="w-full">
                          <div className="flex justify-center">
                            <TotalChip totalFee={noBreakPanel.totalFee} dimmed={noBreakPanel.totalDimmed} />
                          </div>
                        </AnimatedCollapse>
                      </div>
                    }
                  />
                </SurchargeMethodPanel>
              )}

              {recurringScenario === 'break' && (
                <SurchargeMethodPanel
                  key="break"
                  activePanelRef={recurringPanelRef}
                  transitionDuration={recurringFadeDuration}
                >
                  {/* Segment 1 */}
                  <div className={`${TIMELINE_SEGMENT_HEADING_CLASS} mb-1`}>
                    Work Segment 1
                  </div>
                  <ViolationSpans spans={seg1.spans} stacked={false} />
                  <TimelineBar zones={seg1.zones} layoutOpts={{ minPct: 13 }} />
                  <TimeAxis ticks={seg1.ticks} />

                  {/* Reset annotation */}
                  <div className="flex items-center gap-2 my-2">
                    <div className="flex-1 h-px bg-scenario-green/15" />
                    <span className="font-heading text-[10px] sm:text-[12px] uppercase tracking-[1px] whitespace-nowrap text-scenario-green/80">
                      &#8634; Deadline resets — fee schedule does NOT reset
                    </span>
                    <div className="flex-1 h-px bg-scenario-green/15" />
                  </div>

                  {/* Segment 2 */}
                  <div className={`${TIMELINE_SEGMENT_HEADING_CLASS} mb-1`}>
                    Work Segment 2
                  </div>
                  <ViolationSpans spans={seg2.spans} stacked={false} />
                  <TimelineBar zones={seg2.zones} layoutOpts={{ minPct: 13 }} />
                  <TimeAxis
                    ticks={seg2.ticks}
                    endContent={
                      <div data-anim-audit="fixed-fee-total-chip-collapse">
                        <AnimatedCollapse open={!withBreakDimmed} duration={250} className="w-full">
                          <div className="flex justify-center">
                            <TotalChip totalFee={withBreakTotal} dimmed={withBreakDimmed} />
                          </div>
                        </AnimatedCollapse>
                      </div>
                    }
                  />
                </SurchargeMethodPanel>
              )}
            </AnimatePresence>
          </div>
        </div>
        </ScenarioTogglePanel>

        {/* Time-agnostic + schedule-repeat callouts */}
        <FixedFeeCallouts showScheduleRepeat={activeFeeStructureMode === 'schedule'} showNoProration />
      </>
    );
  })();

  const modeContent = isCountMode ? (
    <CountModeBar
      feeSchedule={graphicFeeSchedule}
      requiredBreaks={requiredBreaks}
      scalingEnabled={scalingEnabled}
      showScheduleRepeat={activeFeeStructureMode === 'schedule'}
    />
  ) : (
    <div
      ref={deadlineContainerRef}
      className="overflow-hidden"
      style={deadlineContainerStyle}
      data-anim-audit="fixed-fee-deadline-mode-container"
      data-anim-audit-recurring={recurringDeadline ? 'true' : 'false'}
    >
      <div
        data-anim-audit="fixed-fee-deadline-single-container"
        data-anim-audit-open={!recurringDeadline ? 'true' : 'false'}
      />
      <div
        data-anim-audit="fixed-fee-deadline-recurring-container"
        data-anim-audit-open={recurringDeadline ? 'true' : 'false'}
      />
      <div className="relative grid">
        <AnimatePresence mode="sync" initial={false}>
          {recurringDeadline ? (
            <SurchargeMethodPanel
              key="deadline-recurring"
              activePanelRef={deadlinePanelRef}
              transitionDuration={deadlineFadeDuration}
            >
              {recurringContent}
            </SurchargeMethodPanel>
          ) : (
            <SurchargeMethodPanel
              key="deadline-single"
              activePanelRef={deadlinePanelRef}
              transitionDuration={deadlineFadeDuration}
            >
              {singleContent}
            </SurchargeMethodPanel>
          )}
        </AnimatePresence>
      </div>
    </div>
  );

  return (
    <div
      role="group"
      aria-label="Fixed fee timeline"
      data-anim-audit="fixed-fee-timeline-root"
      data-anim-audit-mode={timelineModeKey}
      data-anim-audit-recurring={recurringDeadline ? 'true' : 'false'}
    >
      <div
        ref={modeContainerRef}
        className="overflow-hidden"
        style={modeContainerStyle}
        data-anim-audit="fixed-fee-mode-container"
      >
        <div className="relative grid">
          <AnimatePresence mode="sync" initial={false}>
            {isCountMode ? (
              <SurchargeMethodPanel
                key="count-mode"
                activePanelRef={modePanelRef}
                transitionDuration={modeFadeDuration}
              >
                {modeContent}
              </SurchargeMethodPanel>
            ) : (
              <SurchargeMethodPanel
                key="deadline-mode"
                activePanelRef={modePanelRef}
                transitionDuration={modeFadeDuration}
              >
                {modeContent}
              </SurchargeMethodPanel>
            )}
          </AnimatePresence>
        </div>
      </div>
      {controlSlot}
    </div>
  );
};
